import { LeadInterface } from "../Leads/Lead.Interface";
import { LeaseInterface } from "../Lease/Lease.Interface";
import { HourOfDay } from "../Shared/DateAndTime.Interfaces";
import { OrgBaseInterface } from "../Shared/OrgBase.Interace";
import { Slug } from "../Shared/Shared.Interfaces";
import { UserInterface } from "../User/User.Interface";
import { LocationVarKey } from "../Variables/Generated/LocationVarKey.Data";
import { UserVarInterface } from "../Variables/UserVariables.Interface";

// LINK libs/entities/src/Sequences/Sequence.Entity.ts

export enum SequenceType {
  PAST_DUE = "PAST_DUE",
  ABANDONED_CART = "ABANDONED_CART",
  MOVE_IN = "MOVE_IN",
  MOVE_OUT = "MOVE_OUT"
}

// export enum SequenceRunType {
//     AD_HOC = "ad hoc",
//     TIME = "time",
// }

/*
Job:
 Every 30 min query the sequence table for sequences where:
 - half hour is true,
 - or hourly is true and it's the top of the hour,
 - or daily at time is true and it's the time specified.

 It then takes those sequences and calls the handler for that sequence type.
*/

export interface SequenceInterface extends OrgBaseInterface {
  id: number;
  orgId: number;
  name: string;
  type: SequenceType;
  subType: SequenceSubType;
  conditions: SequenceConditions;
  questSlug: Slug;

  // runType: SequenceRunType;
  // lastChecked: HourOfDay;  // 24 hour time. Can only be half hour or on the hour.
}

export type SequenceConditions =
  | SequencePastDueData
  | SequenceAbandonCartDay0Data
  | SequenceAbandonCartData
  | SequenceConditionsBase
  | SequenceMoveInDay0Data
  | SequenceMoveInBeyondDay0Data
  | SequenceMoveOutDay0Data
  | SequenceMoveOutBeyondDay0Data;

interface SequenceConditionsBase {
  // runType: SequenceRunType;
  sequenceEnabledVariable: LocationVarKey | "";
}

export interface SequencePastDueInterface extends SequenceInterface {
  conditions: SequencePastDueData;
}

export interface SequencePastMoveInInterface extends SequenceInterface {
  conditions: SequenceMoveInDay0Data | SequenceMoveInBeyondDay0Data;
}

export interface SequencePastMoveOutInterface extends SequenceInterface {
  conditions: SequenceMoveOutDay0Data | SequenceMoveOutBeyondDay0Data;
}

export interface SequenceAbandonCartInterface extends SequenceInterface {
  conditions: SequenceAbandonCartDay0Data | SequenceAbandonCartData;
}

export enum SequenceSubType {
  CUSTOM = "CUSTOM",
  PAST_DUE_DAY_00 = "PAST_DUE_DAY_00",
  PAST_DUE_DAY_01 = "PAST_DUE_DAY_01",
  PAST_DUE_DAY_02 = "PAST_DUE_DAY_02",
  PAST_DUE_DAY_03 = "PAST_DUE_DAY_03",
  PAST_DUE_DAY_05 = "PAST_DUE_DAY_05",
  PAST_DUE_DAY_07 = "PAST_DUE_DAY_07",
  PAST_DUE_DAY_10 = "PAST_DUE_DAY_10",
  PAST_DUE_DAY_14 = "PAST_DUE_DAY_14",
  PAST_DUE_DAY_30 = "PAST_DUE_DAY_30",
  PAST_DUE_DAY_45 = "PAST_DUE_DAY_45",

  ABANDONED_CART_DAY_00 = "ABANDONED_CART_DAY_00",
  ABANDONED_CART_DAY_01 = "ABANDONED_CART_DAY_01",
  ABANDONED_CART_DAY_02 = "ABANDONED_CART_DAY_02",
  ABANDONED_CART_DAY_03 = "ABANDONED_CART_DAY_03",
  ABANDONED_CART_DAY_05 = "ABANDONED_CART_DAY_05",
  ABANDONED_CART_DAY_07 = "ABANDONED_CART_DAY_07",
  ABANDONED_CART_DAY_10 = "ABANDONED_CART_DAY_10",
  ABANDONED_CART_DAY_14 = "ABANDONED_CART_DAY_14",
  ABANDONED_CART_DAY_30 = "ABANDONED_CART_DAY_30",

  MOVE_IN_DAY_00 = "MOVE_IN_DAY_00",
  MOVE_IN_DAY_01 = "MOVE_IN_DAY_01",
  MOVE_IN_DAY_02 = "MOVE_IN_DAY_02",
  MOVE_IN_DAY_03 = "MOVE_IN_DAY_03",
  MOVE_IN_DAY_05 = "MOVE_IN_DAY_05",
  MOVE_IN_DAY_07 = "MOVE_IN_DAY_07",
  MOVE_IN_DAY_10 = "MOVE_IN_DAY_10",
  MOVE_IN_DAY_14 = "MOVE_IN_DAY_14",
  MOVE_IN_DAY_30 = "MOVE_IN_DAY_30",
  MOVE_IN_DAY_90 = "MOVE_IN_DAY_90",

  MOVE_OUT_DAY_00 = "MOVE_OUT_DAY_00",
  MOVE_OUT_DAY_01 = "MOVE_OUT_DAY_01",
  MOVE_OUT_DAY_02 = "MOVE_OUT_DAY_02",
  MOVE_OUT_DAY_03 = "MOVE_OUT_DAY_03",
  MOVE_OUT_DAY_05 = "MOVE_OUT_DAY_05",
  MOVE_OUT_DAY_07 = "MOVE_OUT_DAY_07",
  MOVE_OUT_DAY_10 = "MOVE_OUT_DAY_10",
  MOVE_OUT_DAY_14 = "MOVE_OUT_DAY_14",
  MOVE_OUT_DAY_30 = "MOVE_OUT_DAY_30"
}

export const DefaultSequenceConditions: { [name in SequenceSubType]: SequenceConditions } = {
  [SequenceSubType.CUSTOM]: { sequenceEnabledVariable: "" },
  [SequenceSubType.PAST_DUE_DAY_00]: {
    daysPastDue: 0,
    timeVariable: LocationVarKey.SequencePastDueDay00Time,
    sequenceEnabledVariable: LocationVarKey.SequencePastDueDay00Enabled
  },
  [SequenceSubType.PAST_DUE_DAY_01]: {
    daysPastDue: 1,
    timeVariable: LocationVarKey.SequencePastDueDay01Time,
    sequenceEnabledVariable: LocationVarKey.SequencePastDueDay01Enabled
  },
  [SequenceSubType.PAST_DUE_DAY_02]: {
    daysPastDue: 2,
    timeVariable: LocationVarKey.SequencePastDueDay02Time,
    sequenceEnabledVariable: LocationVarKey.SequencePastDueDay02Enabled
  },
  [SequenceSubType.PAST_DUE_DAY_03]: {
    daysPastDue: 3,
    timeVariable: LocationVarKey.SequencePastDueDay03Time,
    sequenceEnabledVariable: LocationVarKey.SequencePastDueDay03Enabled
  },
  [SequenceSubType.PAST_DUE_DAY_05]: {
    daysPastDue: 5,
    timeVariable: LocationVarKey.SequencePastDueDay05Time,
    sequenceEnabledVariable: LocationVarKey.SequencePastDueDay05Enabled
  },
  [SequenceSubType.PAST_DUE_DAY_07]: {
    daysPastDue: 7,
    timeVariable: LocationVarKey.SequencePastDueDay07Time,
    sequenceEnabledVariable: LocationVarKey.SequencePastDueDay07Enabled
  },
  [SequenceSubType.PAST_DUE_DAY_10]: {
    daysPastDue: 10,
    timeVariable: LocationVarKey.SequencePastDueDay10Time,
    sequenceEnabledVariable: LocationVarKey.SequencePastDueDay10Enabled
  },
  [SequenceSubType.PAST_DUE_DAY_14]: {
    daysPastDue: 14,
    timeVariable: LocationVarKey.SequencePastDueDay14Time,
    sequenceEnabledVariable: LocationVarKey.SequencePastDueDay14Enabled
  },
  [SequenceSubType.PAST_DUE_DAY_30]: {
    daysPastDue: 30,
    timeVariable: LocationVarKey.SequencePastDueDay30Time,
    sequenceEnabledVariable: LocationVarKey.SequencePastDueDay30Enabled
  },
  [SequenceSubType.PAST_DUE_DAY_45]: {
    daysPastDue: 45,
    timeVariable: LocationVarKey.SequencePastDueDay45Time,
    sequenceEnabledVariable: LocationVarKey.SequencePastDueDay45Enabled
  },

  [SequenceSubType.ABANDONED_CART_DAY_00]: {
    daysAgo: 0,
    minTimeVariable: LocationVarKey.SequenceAbandonedCartDay00MinTime,
    maxTimeVariable: LocationVarKey.SequenceAbandonedCartDay00MaxTime,
    sequenceEnabledVariable: LocationVarKey.SequenceAbandonedCartDay00Enabled
  },
  [SequenceSubType.ABANDONED_CART_DAY_01]: {
    daysAgo: 1,
    timeVariable: LocationVarKey.SequenceAbandonedCartDay01Time,
    sequenceEnabledVariable: LocationVarKey.SequenceAbandonedCartDay01Enabled
  },
  [SequenceSubType.ABANDONED_CART_DAY_02]: {
    daysAgo: 2,
    timeVariable: LocationVarKey.SequenceAbandonedCartDay02Time,
    sequenceEnabledVariable: LocationVarKey.SequenceAbandonedCartDay02Enabled
  },
  [SequenceSubType.ABANDONED_CART_DAY_03]: {
    daysAgo: 3,
    timeVariable: LocationVarKey.SequenceAbandonedCartDay03Time,
    sequenceEnabledVariable: LocationVarKey.SequenceAbandonedCartDay03Enabled
  },
  [SequenceSubType.ABANDONED_CART_DAY_05]: {
    daysAgo: 5,
    timeVariable: LocationVarKey.SequenceAbandonedCartDay05Time,
    sequenceEnabledVariable: LocationVarKey.SequenceAbandonedCartDay05Enabled
  },
  [SequenceSubType.ABANDONED_CART_DAY_07]: {
    daysAgo: 7,
    timeVariable: LocationVarKey.SequenceAbandonedCartDay07Time,
    sequenceEnabledVariable: LocationVarKey.SequenceAbandonedCartDay07Enabled
  },
  [SequenceSubType.ABANDONED_CART_DAY_10]: {
    daysAgo: 10,
    timeVariable: LocationVarKey.SequenceAbandonedCartDay10Time,
    sequenceEnabledVariable: LocationVarKey.SequenceAbandonedCartDay10Enabled
  },
  [SequenceSubType.ABANDONED_CART_DAY_14]: {
    daysAgo: 14,
    timeVariable: LocationVarKey.SequenceAbandonedCartDay14Time,
    sequenceEnabledVariable: LocationVarKey.SequenceAbandonedCartDay14Enabled
  },
  [SequenceSubType.ABANDONED_CART_DAY_30]: {
    daysAgo: 30,
    timeVariable: LocationVarKey.SequenceAbandonedCartDay30Time,
    sequenceEnabledVariable: LocationVarKey.SequenceAbandonedCartDay30Enabled
  },

  [SequenceSubType.MOVE_IN_DAY_00]: {
    daysSince: 0,
    minTimeVariable: LocationVarKey.SequenceMoveInDay00MinTime,
    maxTimeVariable: LocationVarKey.SequenceMoveInDay00MaxTime,
    sequenceEnabledVariable: LocationVarKey.SequenceMoveInDay00Enabled
  },

  [SequenceSubType.MOVE_IN_DAY_01]: {
    daysSince: 1,
    timeVariable: LocationVarKey.SequenceMoveInDay01Time,
    sequenceEnabledVariable: LocationVarKey.SequenceMoveInDay01Enabled
  },
  [SequenceSubType.MOVE_IN_DAY_02]: {
    daysSince: 2,
    timeVariable: LocationVarKey.SequenceMoveInDay02Time,
    sequenceEnabledVariable: LocationVarKey.SequenceMoveInDay02Enabled
  },
  [SequenceSubType.MOVE_IN_DAY_03]: {
    daysSince: 3,
    timeVariable: LocationVarKey.SequenceMoveInDay03Time,
    sequenceEnabledVariable: LocationVarKey.SequenceMoveInDay03Enabled
  },
  [SequenceSubType.MOVE_IN_DAY_05]: {
    daysSince: 5,
    timeVariable: LocationVarKey.SequenceMoveInDay05Time,
    sequenceEnabledVariable: LocationVarKey.SequenceMoveInDay05Enabled
  },
  [SequenceSubType.MOVE_IN_DAY_07]: {
    daysSince: 7,
    timeVariable: LocationVarKey.SequenceMoveInDay07Time,
    sequenceEnabledVariable: LocationVarKey.SequenceMoveInDay07Enabled
  },
  [SequenceSubType.MOVE_IN_DAY_10]: {
    daysSince: 10,
    timeVariable: LocationVarKey.SequenceMoveInDay10Time,
    sequenceEnabledVariable: LocationVarKey.SequenceMoveInDay10Enabled
  },
  [SequenceSubType.MOVE_IN_DAY_14]: {
    daysSince: 14,
    timeVariable: LocationVarKey.SequenceMoveInDay14Time,
    sequenceEnabledVariable: LocationVarKey.SequenceMoveInDay14Enabled
  },
  [SequenceSubType.MOVE_IN_DAY_30]: {
    daysSince: 30,
    timeVariable: LocationVarKey.SequenceMoveInDay30Time,
    sequenceEnabledVariable: LocationVarKey.SequenceMoveInDay30Enabled
  },
  [SequenceSubType.MOVE_IN_DAY_90]: {
    daysSince: 90,
    timeVariable: LocationVarKey.SequenceMoveInDay90Time,
    sequenceEnabledVariable: LocationVarKey.SequenceMoveInDay90Enabled
  },

  [SequenceSubType.MOVE_OUT_DAY_00]: {
    daysSince: 0,
    minTimeVariable: LocationVarKey.SequenceMoveOutDay00MinTime,
    maxTimeVariable: LocationVarKey.SequenceMoveOutDay00MaxTime,
    sequenceEnabledVariable: LocationVarKey.SequenceMoveOutDay00Enabled
  },

  [SequenceSubType.MOVE_OUT_DAY_01]: {
    daysSince: 1,
    timeVariable: LocationVarKey.SequenceMoveOutDay01Time,
    sequenceEnabledVariable: LocationVarKey.SequenceMoveOutDay01Enabled
  },
  [SequenceSubType.MOVE_OUT_DAY_02]: {
    daysSince: 2,
    timeVariable: LocationVarKey.SequenceMoveOutDay02Time,
    sequenceEnabledVariable: LocationVarKey.SequenceMoveOutDay02Enabled
  },
  [SequenceSubType.MOVE_OUT_DAY_03]: {
    daysSince: 3,
    timeVariable: LocationVarKey.SequenceMoveOutDay03Time,
    sequenceEnabledVariable: LocationVarKey.SequenceMoveOutDay03Enabled
  },
  [SequenceSubType.MOVE_OUT_DAY_05]: {
    daysSince: 5,
    timeVariable: LocationVarKey.SequenceMoveOutDay05Time,
    sequenceEnabledVariable: LocationVarKey.SequenceMoveOutDay05Enabled
  },
  [SequenceSubType.MOVE_OUT_DAY_07]: {
    daysSince: 7,
    timeVariable: LocationVarKey.SequenceMoveOutDay07Time,
    sequenceEnabledVariable: LocationVarKey.SequenceMoveOutDay07Enabled
  },
  [SequenceSubType.MOVE_OUT_DAY_10]: {
    daysSince: 10,
    timeVariable: LocationVarKey.SequenceMoveOutDay10Time,
    sequenceEnabledVariable: LocationVarKey.SequenceMoveOutDay10Enabled
  },
  [SequenceSubType.MOVE_OUT_DAY_14]: {
    daysSince: 14,
    timeVariable: LocationVarKey.SequenceMoveOutDay14Time,
    sequenceEnabledVariable: LocationVarKey.SequenceMoveOutDay14Enabled
  },
  [SequenceSubType.MOVE_OUT_DAY_30]: {
    daysSince: 30,
    timeVariable: LocationVarKey.SequenceMoveOutDay30Time,
    sequenceEnabledVariable: LocationVarKey.SequenceMoveOutDay30Enabled
  }
};

export const DefaultSequenceQuestSlug: { [name in SequenceSubType]: Slug | undefined } = {
  [SequenceSubType.CUSTOM]: undefined,
  [SequenceSubType.PAST_DUE_DAY_00]: "past-due-0",
  [SequenceSubType.PAST_DUE_DAY_01]: "past-due-1",
  [SequenceSubType.PAST_DUE_DAY_02]: "past-due-2",
  [SequenceSubType.PAST_DUE_DAY_03]: "past-due-3",
  [SequenceSubType.PAST_DUE_DAY_05]: "past-due-5",
  [SequenceSubType.PAST_DUE_DAY_07]: "past-due-7",
  [SequenceSubType.PAST_DUE_DAY_10]: "past-due-10",
  [SequenceSubType.PAST_DUE_DAY_14]: "past-due-14",
  [SequenceSubType.PAST_DUE_DAY_30]: "past-due-30",
  [SequenceSubType.PAST_DUE_DAY_45]: "past-due-45",

  [SequenceSubType.ABANDONED_CART_DAY_00]: "abandoned-cart-0",
  [SequenceSubType.ABANDONED_CART_DAY_01]: "abandoned-cart-1",
  [SequenceSubType.ABANDONED_CART_DAY_02]: "abandoned-cart-2",
  [SequenceSubType.ABANDONED_CART_DAY_03]: "abandoned-cart-3",
  [SequenceSubType.ABANDONED_CART_DAY_05]: "abandoned-cart-5",
  [SequenceSubType.ABANDONED_CART_DAY_07]: "abandoned-cart-7",
  [SequenceSubType.ABANDONED_CART_DAY_10]: "abandoned-cart-10",
  [SequenceSubType.ABANDONED_CART_DAY_14]: "abandoned-cart-14",
  [SequenceSubType.ABANDONED_CART_DAY_30]: "abandoned-cart-30",

  [SequenceSubType.MOVE_IN_DAY_00]: "move-in-0",
  [SequenceSubType.MOVE_IN_DAY_01]: "move-in-1",
  [SequenceSubType.MOVE_IN_DAY_02]: "move-in-2",
  [SequenceSubType.MOVE_IN_DAY_03]: "move-in-3",
  [SequenceSubType.MOVE_IN_DAY_05]: "move-in-5",
  [SequenceSubType.MOVE_IN_DAY_07]: "move-in-7",
  [SequenceSubType.MOVE_IN_DAY_10]: "move-in-10",
  [SequenceSubType.MOVE_IN_DAY_14]: "move-in-14",
  [SequenceSubType.MOVE_IN_DAY_30]: "move-in-30",
  [SequenceSubType.MOVE_IN_DAY_90]: "move-in-90",

  [SequenceSubType.MOVE_OUT_DAY_00]: "move-out-0",
  [SequenceSubType.MOVE_OUT_DAY_01]: "move-out-1",
  [SequenceSubType.MOVE_OUT_DAY_02]: "move-out-2",
  [SequenceSubType.MOVE_OUT_DAY_03]: "move-out-3",
  [SequenceSubType.MOVE_OUT_DAY_05]: "move-out-5",
  [SequenceSubType.MOVE_OUT_DAY_07]: "move-out-7",
  [SequenceSubType.MOVE_OUT_DAY_10]: "move-out-10",
  [SequenceSubType.MOVE_OUT_DAY_14]: "move-out-14",
  [SequenceSubType.MOVE_OUT_DAY_30]: "move-out-30"
};

export interface SequencePastDueData extends SequenceConditionsBase {
  daysPastDue: number;
  timeVariable: LocationVarKey;
  sequenceEnabledVariable: LocationVarKey; // This will be a HH:MM value in EST.
}

export interface SequenceMoveInDay0Data extends SequenceConditionsBase {
  daysSince: 0;
  minTimeVariable: LocationVarKey;
  maxTimeVariable: LocationVarKey;
  sequenceEnabledVariable: LocationVarKey; // This will be a HH:MM value in EST.
}

export interface SequenceMoveInBeyondDay0Data extends SequenceConditionsBase {
  daysSince: number;
  timeVariable: LocationVarKey;
  sequenceEnabledVariable: LocationVarKey; // This will be a HH:MM value in EST.
}

export interface SequenceMoveOutDay0Data extends SequenceConditionsBase {
  daysSince: 0;
  minTimeVariable: LocationVarKey;
  maxTimeVariable: LocationVarKey;
  sequenceEnabledVariable: LocationVarKey; // This will be a HH:MM value in EST.
}

export interface SequenceMoveOutBeyondDay0Data extends SequenceConditionsBase {
  daysSince: number;
  timeVariable: LocationVarKey;
  sequenceEnabledVariable: LocationVarKey; // This will be a HH:MM value in EST.
}

export interface SequenceAbandonCartDay0Data extends SequenceConditionsBase {
  daysAgo: 0;
  minTimeVariable: LocationVarKey;
  maxTimeVariable: LocationVarKey;
  sequenceEnabledVariable: LocationVarKey;
}

export interface SequenceAbandonCartData extends SequenceConditionsBase {
  daysAgo: number;
  timeVariable: LocationVarKey;
  sequenceEnabledVariable: LocationVarKey; // This will be a HH:MM value in EST.
}

export enum SequenceEnabledStatus {
  ENABLED = "ENABLED",
  DISABLED = "DISABLED",
  SIMULATED = "SIMULATED"
}

export enum SequenceUserEventStatus {
  READY = "READY",
  QUEUED = "QUEUED",
  PROCESSING = "PROCESSING",
  SENT = "SENT",
  FAILED = "ERROR", // Not sure what we will use this for yet.
  SIMULATED = "SIMULATED", // Thi is used for testing on staging etc.
  SUPPRESSED = "SUPPRESSED" // JIT Lookup (ENG-2870): send suppressed at send time after re-qualification. Terminal, not retried.
}

export enum SequenceUserEventHistoryItemType {
  ERROR = "ERROR",
  CREATED = "CREATED",
  UPDATE = "UPDATE"
}

export interface SequenceUserEventHistoryItemInterface {
  createdAt: Date;
  type: SequenceUserEventHistoryItemType;
  message: string;
}

export interface SequenceUserEventInterface extends OrgBaseInterface {
  user: UserInterface;
  leaseId?: number;
  leadId?: number;
  locationId?: number;
  variables?: { [key: string]: string };
  sequence: SequenceInterface;
  sequenceSubType: SequenceSubType;

  status: SequenceUserEventStatus;
  reason?: string;
  questSlug: Slug;
  record: SequenceRecordInterface;
  recordId: number;
  history: SequenceUserEventHistoryItemInterface[];
  nextRetryAt?: Date;
  sentAt?: Date;
  queuedAt?: Date;
  startedProcessingAt?: Date;
  dedupKey?: string;
}

export interface SequenceUserData {
  user: UserInterface;
  lease?: LeaseInterface;
  lead?: LeadInterface;
  variables?: UserVarInterface[];
  locationId?: number;
  date?: string;
  simulated: boolean;
  dedupKey?: string;
}

export interface SequenceRecordInterface extends OrgBaseInterface {
  sequence: SequenceInterface;
  sequenceSubType: SequenceSubType;
  numberOfUsers: number;
  numberOfLocations: number;
  locationData?: { [locationId: string]: number };
  hadUsers: boolean;
  eventsCreated: number;
  simulatedEvents: number;
  time: HourOfDay;
}
